import PublicPage, { Section, Bullets } from "@/components/PublicPage";
import { COMPANY } from "@/lib/company";

export default function Disclaimer() {
  return (
    <PublicPage
      testId="disclaimer-page"
      title="Disclaimer"
      intro="Plan-Sarthi is a decision-support platform. It computes and presents information from the data your team enters. It does not, and cannot, replace the judgement of a qualified engineer, planner, quantity surveyor or accountant."
    >
      <Section n={1} title="Outputs are advisory, not professional advice">
        <p>
          Every figure the platform produces — progress percentages, balance quantities, run-rates, critical path and
          float, S-curves, earned value indices (CPI, SPI, EAC, VAC), forecast completion dates, risk bands, health
          scores, what-if scenarios and AI-written narratives — is an analytical output. It is provided for internal
          planning and monitoring purposes only.
        </p>
        <p>
          Nothing on the platform constitutes engineering, architectural, structural, safety, legal, tax, accounting
          or investment advice. It must not be used as the sole basis for any design, construction, contractual,
          commercial or safety decision. Always have outputs reviewed by suitably qualified professionals against the
          actual site conditions, drawings, contract documents and applicable codes and standards.
        </p>
      </Section>

      <Section n={2} title="Accuracy depends entirely on your input">
        <p>
          The platform is a calculation and reporting engine. Its outputs are only as accurate as the data supplied to
          it. Errors, omissions, delays, duplicate entries, wrong units, wrong rates, unvalidated DPRs or an
          incomplete WBS will produce misleading results, however confidently they are presented.
        </p>
        <Bullets
          items={[
            "Quantities, units and rates must be reconciled against the signed BOQ and contract.",
            "Daily Progress Reports must be validated by an authorised person before being relied upon.",
            "Baseline dates and logic links must reflect the approved programme, or the critical path will be wrong.",
            "Imported BOQ rows produced by the AI parser must be reviewed line by line before they are confirmed.",
          ]}
        />
      </Section>

      <Section n={3} title="Artificial intelligence features">
        <p>
          The Risk Radar narrative and the Smart BOQ Parser use third-party large language models. These systems are
          probabilistic. They can misread a document, mis-classify an item, omit a row or state something plainly
          incorrect with apparent confidence — behaviour commonly described as hallucination.
        </p>
        <Bullets
          items={[
            "Treat every AI output as a draft requiring human verification.",
            "Confidence scores are heuristic indicators, not guarantees of correctness.",
            "Forecasts are extrapolations from historical progress. They assume conditions continue and cannot foresee weather, strikes, design changes, statutory delays, supply-chain failures, force majeure or client-side decisions.",
            "Do not submit outputs to a client, lender, auditor or authority without independent verification and professional sign-off.",
          ]}
        />
      </Section>

      <Section n={4} title="No liability for project outcomes">
        <p>
          {COMPANY.legalName} accepts no liability whatsoever for any project delay, schedule slippage, cost overrun,
          liquidated damages, penalty, disallowed claim, rejected variation, safety incident, rework, loss of profit
          or reputational harm arising from, or claimed to arise from, the use of Plan-Sarthi, its calculations, its
          forecasts, its AI outputs or its reports — or from any decision, instruction or omission based on them.
        </p>
        <p>
          Responsibility for project execution, statutory compliance, site safety, contractual notices and commercial
          decisions rests entirely with your organisation and its appointed professionals. Our liability is further
          limited as set out in clause 9 of the Terms of Service.
        </p>
      </Section>

      <Section n={5} title="Availability, third parties and external links">
        <p>
          The platform is provided on an "as is" and "as available" basis, without warranty of any kind, express or
          implied, including any implied warranty of merchantability or fitness for a particular purpose. Features
          depend on third-party services — payment, email, hosting and AI providers — whose interruption may affect
          availability. Any external link is provided for convenience; we do not endorse and are not responsible for
          third-party content.
        </p>
      </Section>

      <Section n={6} title="Questions">
        <p>
          If anything on the platform is unclear, or a figure does not look right, stop and ask us before acting on
          it: <strong>{COMPANY.email}</strong>. This Disclaimer forms part of the Terms of Service and is governed by
          the laws of India, with exclusive jurisdiction at {COMPANY.jurisdiction}.
        </p>
      </Section>
    </PublicPage>
  );
}
